import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { CardMedia, Input, TextField, Button, MenuItem, Select, FormHelperText } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import CloseIcon from '@mui/icons-material/Close';
import React from "react";
import { UploadMedia } from "../../utils/mediaUpload";
import { showError, showToast } from "../../constants/toasts";
import { useLazyGetAllRolesQuery } from "../../services/accessRole";
import { useAddUserMutation, useLazyGetUsersByIdQuery, useUpdateUserMutation } from "../../services/customer";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";

const AddSubadmin = () => {

  const navigate = useNavigate();
  const { id } = useParams();
  const [image, setImage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>()
  const [roles, setRoles] = useState<any[]>([])
  const [getRoles] = useLazyGetAllRolesQuery()
  const [addSubadmin] = useAddUserMutation()
  const [updateSubAdmin] = useUpdateUserMutation()
  const [getById] = useLazyGetUsersByIdQuery()
  const [user, setUser] = useState<any>(null)

  const fetchRoles = async () => {
    setLoading(true)
    try {
      const response = await getRoles({
        from: 1,
        size: 1000,
        search: "",
      }).unwrap();

      if (response?.statusCode === 200) {
        setRoles(response.data.data || []);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch roles");
    }
    finally {
      setLoading(false)
    }
  };


  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setImage(imageUrl.data);
      formik.setFieldValue('image', imageUrl.data)
    }
  };
  const fetchUser = async () => {
    try {
      if (!id) return;
      setLoading(true)
      const res = await getById({ id }).unwrap();
      if (res?.statusCode === 200) {
        setUser(res.data)
        setImage(res?.data?.image || "")
      }
    } catch (error: any) {
      showError(error?.data?.message || error?.message)
    } finally {
      setLoading(false)
    }
  }
  const initialValues = useMemo(() => ({
    image: user?.image || "",
    firstName: user?.firstName || "",
    callingCode: user?.callingCode || "IN",
    phoneNumber: (() => {
      const rawPhone = user?.phoneNumber ? String(user?.phoneNumber) : "";
      const cc = user?.countryCode ? String(user?.countryCode) : "";
      if (!rawPhone || !cc) return rawPhone;
      const ccNoPlus = cc.startsWith("+") ? cc.substring(1) : cc;
      if (rawPhone.startsWith(cc)) return rawPhone.substring(cc.length);
      if (rawPhone.startsWith(ccNoPlus)) return rawPhone.substring(ccNoPlus.length);
      return rawPhone;
    })(),
    email: user?.email || "",
    accessRoleId: (user?.accessRoleId && typeof user?.accessRoleId === "object")
      ? (user?.accessRoleId as any)?._id || ""
      : (user?.accessRoleId || ""),
    countryCode: user?.countryCode || "+91",
  }), [user])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      image: Yup.string().nullable(),
      firstName: Yup.string().required("Name is required"),
      phoneNumber: Yup.string()
        .required("Phone number is required")
        .matches(/^\d{8,15}$/, "Enter a valid phone number"),
      email: Yup.string().required("Email is required").email("Enter a valid email"),
      accessRoleId: Yup.string().required("Role is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const buildBodyFromValues = (v: any) => ({
          image: v.image,
          firstName: v.firstName,
          email: v.email,
          accessRoleId: v.accessRoleId,
          phoneNumber: `${(v?.countryCode || "").replace(/^([^+])/, "+$1")}${v?.phoneNumber || ""}`,
          countryCode: (v?.countryCode || "").startsWith("+") ? v.countryCode : `+${v?.countryCode || ""}`,
          callingCode: v?.callingCode,
        });

        const currentBody: any = buildBodyFromValues(values);
        const initialBody: any = buildBodyFromValues(formik.initialValues);
        const changedBody: any = Object.keys(currentBody).reduce((acc: any, key: string) => {
          if (currentBody[key] !== initialBody[key]) acc[key] = currentBody[key];
          return acc;
        }, {});
        let response
        if (id) {
          if (Object.keys(changedBody).length === 0) {
            showToast("No changes to update")
            setSubmitting(false)
            return
          }
          response = await updateSubAdmin({ id, body: changedBody }).unwrap()
        } else {
          response = await addSubadmin({ body: { ...currentBody, role: "SUB_ADMIN" } }).unwrap()
        }
        if (response?.statusCode === 200) {
          showToast(id ? "Sub-admin updated" : "Sub-admin added")
          navigate("/manage-subadmin")
        }
      } catch (error: any) {
        showError(error?.data?.message || error?.message || "Failed to save")
      } finally {
        setSubmitting(false)
      }
    }
  })
  const handlePhoneChange = (phoneValue: any, country: any) => {
    const dialCode = country?.dialCode ? String(country.dialCode) : "";
    let phoneWithoutCode = String(phoneValue || "");
    if (dialCode && phoneWithoutCode.startsWith(dialCode)) {
      phoneWithoutCode = phoneWithoutCode.substring(dialCode.length);
    }
    formik.setFieldValue("phoneNumber", phoneWithoutCode);
    const codeWithPlus = dialCode ? `+${dialCode}` : "";
    formik.setFieldValue("countryCode", codeWithPlus);
  };
  useEffect(() => {
    fetchRoles()
  }, [])
  useEffect(() => {
    fetchUser()
  }, [id])
  return (
    <MainContainer>
      <div className="main_title">
        <h1>{id ? "Edit" : "Add"} Sub-Admin</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span>
          {' '} - <span onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</span>
          {' '} - {id ? "Edit" : "Add"} Sub-Admin
        </p>
      </div>
      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Upload Image</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={image}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        formik.setFieldValue('image', "")
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label
                  className="upload_image"
                  htmlFor="icon-button-file"
                >
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={(event) => {
                      handleImageUpload(event as any)
                        ; (event.target as any).value = ""
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="icon"
                        />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_33">
              <label>Name</label>
              <TextField
                placeholder="Enter Name"
                hiddenLabel
                fullWidth
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.firstName && (formik.errors.firstName as any)}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              />
            </div>
            <div className="control_group w_33">
              <label>Email Id</label>
              <TextField
                placeholder="Enter Email Id"
                hiddenLabel
                fullWidth
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.email && (formik.errors.email as any)}
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
            </div>
            <div className="control_group w_50">
              <label>Phone Number</label>
              <PhoneInput
                placeholder="Enter phone number"
                enableSearch
                value={`${(formik.values.countryCode || '').replace('+', '')}${formik.values.phoneNumber || ''}`}
                country={"in"}
                onChange={(phone, country) =>
                  handlePhoneChange(phone, country)
                }
                onBlur={() => formik.setFieldTouched('phoneNumber', true)}
                inputStyle={{
                  width: "100%",
                  border:
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                      ? "1px solid #d32f2f"
                      : "1px solid #ccc",
                }}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <FormHelperText error>
                  {formik.errors.phoneNumber as any}
                </FormHelperText>
              )}
            </div>
            <div className="control_group w_33">
              <label>Role</label>
              <Select
                fullWidth
                displayEmpty
                name="accessRoleId"
                value={formik.values.accessRoleId}
                onChange={(e) => formik.setFieldValue('accessRoleId', e.target.value)}
              >
                <MenuItem disabled value="">Select Role</MenuItem>
                {roles.map((r: any) => (
                  <MenuItem key={r?._id} value={r?._id}>{r?.name}</MenuItem>
                ))}
              </Select>
              {formik.touched.accessRoleId && formik.errors.accessRoleId && (
                <span style={{ color: '#d32f2f', fontSize: 12 }}>{formik.errors.accessRoleId as any}</span>
              )}
            </div>
          </div>
          <form className="form_btn" onSubmit={formik.handleSubmit}>
            <Button className="btn btn_primary" type="submit" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </form>
        </div>
      </div>
    </MainContainer>
  );
};
export default AddSubadmin;

